import { Button } from '@/components/ui/button';
import { useDebouncedCallback } from '@/hooks/use-debounce';
import { useUpdateVacancy } from '@/hooks/use-recruitment';
import type { VacancyDetail } from '@/types/recruitment';
import { AlertCircle, Check, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { VacancyDescriptionEditor } from './vacancy-description-editor';

const AUTOSAVE_DEBOUNCE_MS = 3000;
const SAVED_INDICATOR_TIMEOUT_MS = 3000;

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface VacancyDescriptionTabProps {
  vacancy: VacancyDetail;
}

export function VacancyDescriptionTab({ vacancy }: VacancyDescriptionTabProps) {
  const initialContent = vacancy.descriptionHtml ?? '';
  const isPublished = vacancy.publicationStatus === 'PUBLISHED';

  const [content, setContent] = useState(initialContent);
  const [lastSavedContent, setLastSavedContent] = useState(initialContent);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

  const updateMutation = useUpdateVacancy({ suppressGlobalToast: true });
  const isDirty = content !== lastSavedContent;

  // Save logic shared between auto-save and explicit save
  const performSave = useCallback(
    async (htmlToSave: string) => {
      if (htmlToSave === lastSavedContent) return;
      setSaveStatus('saving');
      try {
        await updateMutation.mutateAsync({ id: vacancy.id, descriptionHtml: htmlToSave });
        setLastSavedContent(htmlToSave);
        setLastSavedAt(new Date());
        setSaveStatus('saved');
      } catch {
        setSaveStatus('error');
      }
    },
    [lastSavedContent, updateMutation, vacancy.id],
  );

  // Keep latest performSave reference so the debounced wrapper sees fresh state
  const performSaveRef = useRef(performSave);
  useEffect(() => {
    performSaveRef.current = performSave;
  }, [performSave]);

  const debouncedSave = useDebouncedCallback((html: string) => {
    void performSaveRef.current(html);
  }, AUTOSAVE_DEBOUNCE_MS);

  // Auto-revert "saved" indicator to "idle" after a short delay
  useEffect(() => {
    if (saveStatus !== 'saved') return;
    const timer = setTimeout(() => setSaveStatus('idle'), SAVED_INDICATOR_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [saveStatus]);

  // Flush pending save on unmount (e.g., switching tabs)
  useEffect(() => {
    return () => {
      debouncedSave.flush();
    };
  }, [debouncedSave]);

  // beforeunload warning when there are unsaved changes
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const handleEditorChange = useCallback(
    (html: string) => {
      setContent(html);
      if (isPublished) {
        // Explicit save mode — no auto-save, no indicator update
        return;
      }
      setSaveStatus('saving');
      debouncedSave(html);
    },
    [debouncedSave, isPublished],
  );

  const handleExplicitSave = () => {
    void performSave(content);
  };

  return (
    <div className="space-y-4">
      <VacancyDescriptionEditor content={content} onChange={handleEditorChange} />

      {isPublished && (
        <div className="flex items-start gap-2 rounded-md bg-yellow-500/10 border border-yellow-500/30 p-3 text-sm">
          <AlertCircle className="h-4 w-4 mt-0.5 text-yellow-500 flex-shrink-0" />
          <div className="flex-1 text-yellow-100">
            This vacancy is published. Auto-save is disabled — changes will go live on the career site
            only after you click <span className="font-semibold">Save</span>.
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        <SaveStatusIndicator
          status={saveStatus}
          lastSavedAt={lastSavedAt}
          onRetry={handleExplicitSave}
        />
        {isPublished && (
          <Button
            variant="accent"
            onClick={handleExplicitSave}
            disabled={!isDirty || updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        )}
      </div>
    </div>
  );
}

interface SaveStatusIndicatorProps {
  status: SaveStatus;
  lastSavedAt: Date | null;
  onRetry: () => void;
}

function SaveStatusIndicator({ status, lastSavedAt, onRetry }: SaveStatusIndicatorProps) {
  // Track relative time text and refresh every 30s
  const [, forceRender] = useState(0);
  useEffect(() => {
    if (status !== 'idle' || !lastSavedAt) return;
    const interval = setInterval(() => forceRender((n) => n + 1), 30_000);
    return () => clearInterval(interval);
  }, [status, lastSavedAt]);

  if (status === 'saving') {
    return (
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Saving...
      </div>
    );
  }

  if (status === 'saved') {
    return (
      <div className="flex items-center gap-1.5 text-xs text-hygge">
        <Check className="h-3.5 w-3.5" />
        Saved just now
      </div>
    );
  }

  if (status === 'error') {
    return (
      <button
        type="button"
        onClick={onRetry}
        className="flex items-center gap-1.5 text-xs text-destructive hover:underline"
      >
        <AlertCircle className="h-3.5 w-3.5" />
        Failed to save — Retry
      </button>
    );
  }

  // idle
  if (lastSavedAt) {
    return <div className="text-xs text-muted-foreground">Saved {formatRelativeTime(lastSavedAt)}</div>;
  }
  return null;
}

function formatRelativeTime(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return date.toLocaleDateString();
}
